import type { Ref } from 'vue';
import type { HekateDisplayForm } from '@onshape/www-inertia';
import FluentWaitUtil from '../../../../../../../utils/FluentWaitUtil';
import type UnifiedSignupSessionInfo from '../session-info/UnifiedSignupSessionInfo';
import AbstractSignupScreen from './AbstractSignupScreen';
import UnifiedSignupPersonaDetails from '../session-info/UnifiedSignupPersonaDetails';
import UnifiedSignupPersona from '../session-info/UnifiedSignupPersona';
import UnifiedSignupPlan from '../session-info/UnifiedSignupPlan';
import UnifiedSignupIntent from '../session-info/UnifiedSignupIntent';

export default abstract class AbstractSignupScreenWithNewsletterForm extends AbstractSignupScreen {
  protected readonly signupSurveyDiv: HTMLDivElement | null;

  constructor({
    sessionInfo,
    formDefinition,
    isBackButtonHidden,
    signupSurveyDiv,
  }: {
    sessionInfo: Ref<UnifiedSignupSessionInfo>;
    formDefinition: Ref<HekateDisplayForm>;
    isBackButtonHidden: Ref<boolean>;
    signupSurveyDiv: HTMLDivElement | null;
  }) {
    super({ sessionInfo, formDefinition, isBackButtonHidden });
    this.signupSurveyDiv = signupSurveyDiv;
  }

  public override async initListeners({
    partialDiv,
  }: {
    partialDiv: HTMLDivElement;
  }): Promise<void> {
    // wait up to 5 seconds for the resolutionActions to be set, if not we will likely show youtube link
    let replacement: Node = document.createTextNode('');
    let procedure = '';
    const resolutions = await FluentWaitUtil.whileNotFalsy({
      maxTries: 50,
      timeBetweenChecksInMilliseconds: 100,
      provider: () => this.sessionInfo.value.resolutionActions,
    }).catch(() => {
      console.warn(`Timed out waiting for resolutionActions on screen: ${this.screenName}`);
      return null;
    });
    // if we are not on the success screen and we haven't resolved the resolutions, we will not show the survey
    if (resolutions === null && this.screenName !== 'uss-signup-success') {
      console.warn(
        `No resolutionActions found, not showing survey on non-success screen: ${this.screenName}`
      );
      return;
    }
    if (resolutions !== null) {
      procedure = resolutions[0]?.procedure || '';
      replacement = document.createTextNode(resolutions[0]?.params?.replacement || '');
    }

    const { personaDetails: chosenPersonaDetails } = this.sessionInfo.value;
    const { chosenPlan } = this.sessionInfo.value;
    const { intent: chosenIntent } = this.sessionInfo.value;
    const { persona: chosenPersona } = this.sessionInfo.value;
    if (!replacement || !chosenPlan || !chosenIntent || !chosenPersona) {
      return;
    }

    // if persona is startup, show startup survey
    if (chosenPersonaDetails?.compareTo(UnifiedSignupPersonaDetails.startingNewBusiness)) {
      const SignupSurveyStartupProgramElement = partialDiv.querySelector(
        '#signup-survey-startup-program'
      );
      if (SignupSurveyStartupProgramElement) {
        SignupSurveyStartupProgramElement.classList.remove('d-none');
      }
      // if persona is pro or biz intent show link to upcoming events
    } else if (
      UnifiedSignupPlan.pro.compareTo(chosenPlan) ||
      UnifiedSignupIntent.bizIntent.compareTo(chosenIntent) ||
      UnifiedSignupPersona.proPerson.compareTo(chosenPersona)
    ) {
      const signupSurveyUpcomingSessionsElement = partialDiv.querySelector(
        '#signup-survey-upcoming-sessions'
      );
      if (signupSurveyUpcomingSessionsElement) {
        signupSurveyUpcomingSessionsElement.classList.remove('d-none');
      }
      // if not pro and procedure is tellMeMoreAboutIt, show tell us more
    } else if (procedure === 'tellMeMoreAboutIt' && !UnifiedSignupPlan.pro.compareTo(chosenPlan)) {
      if (!this.signupSurveyDiv) {
        console.error('Signup survey div not set');
        return;
      }

      const signupSurveyTellUsMoreElement = partialDiv.querySelector('#signup-survey-tell-us-more');
      if (signupSurveyTellUsMoreElement) {
        signupSurveyTellUsMoreElement.classList.remove('d-none');
      }
      partialDiv.querySelector('#hekate-tell-more-about-replacement')?.appendChild(replacement);
      partialDiv.querySelector('#signup-survey-tell-us-more')?.appendChild(this.signupSurveyDiv);
      const howDidYouHearAboutUsElement = partialDiv.querySelector(
        '[field-name="howDidYouHearAboutUs"]'
      );
      if (howDidYouHearAboutUsElement) {
        const labelElement = howDidYouHearAboutUsElement.querySelector('label') as HTMLLabelElement;
        labelElement.innerHTML = '';
      }
      const signupSurveyElement = partialDiv.querySelector('#signup-survey');
      if (signupSurveyElement) {
        signupSurveyElement.classList.remove('d-none');
      }
      const signupSurveySurveyFormElement = partialDiv.querySelector('#signup-survey-surveyForm');
      if (signupSurveySurveyFormElement) {
        signupSurveySurveyFormElement.classList.remove('d-none');
      }
    } else {
      const signupSurveyYoutubeLinkElement = partialDiv.querySelector(
        '#signup-survey-youtube-link'
      );
      if (signupSurveyYoutubeLinkElement) {
        signupSurveyYoutubeLinkElement.classList.remove('d-none');
      }
    }
  }
}
